import { useTranslation } from 'react-i18next';
import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';
import { AdminSettingsFormWrapper } from 'components/shared/AdminSettings/AdminSettingsFormWrapper';
import { transformAdminSettingsToRequestValues } from '../../../../utils';
import { useSaveResource } from 'RestAPIs/useSaveResource';
import { ADMIN_SETTINGS_GROUP } from 'RestAPIs/endpoints';
import { convertArrayToObjectByKey } from '../../../Tables/Settings/DTRRules/components/utils/convertArrayToObjectByKey';
import { ControlledCheckbox } from 'components/shared/ControlledFields';
import { useFormContext } from 'react-hook-form';
import { convertToInteger, pwdStrategySchema } from '../utils';
import { GROUP_NAME_PWD_RULES } from '../usePwdUserSettingsMultipleEndpoint';
import { pwdStrategyFormData, TabProps } from '../types';

export const PwdStrategySettings = ({ userAndPwdSettings }: TabProps) => {
  const { isLoading, mutate: save } = useSaveResource({
    url: ADMIN_SETTINGS_GROUP,
    id: userAndPwdSettings.nameAndPwdSettings[0].groupId,
    invalidateQueryKey: [GROUP_NAME_PWD_RULES]
  });

  const currentValue = {
    password_min_length: convertToInteger(
      convertArrayToObjectByKey(
        userAndPwdSettings.nameAndPwdSettings[0]?.settings,
        'key'
      )['password.min.length'].value
    ),
    password_min_digits: convertToInteger(
      convertArrayToObjectByKey(
        userAndPwdSettings.nameAndPwdSettings[0]?.settings,
        'key'
      )['password.min.digits'].value
    ),
    password_min_chars: convertToInteger(
      convertArrayToObjectByKey(
        userAndPwdSettings.nameAndPwdSettings[0]?.settings,
        'key'
      )['password.min.chars'].value
    ),
    password_max_retry: convertToInteger(
      convertArrayToObjectByKey(
        userAndPwdSettings.nameAndPwdSettings[0]?.settings,
        'key'
      )['password.max.retry'].value
    ),
    password_validity: convertToInteger(
      convertArrayToObjectByKey(
        userAndPwdSettings.nameAndPwdSettings[0]?.settings,
        'key'
      )['password.validity'].value
    ),
    password_unique:
      convertArrayToObjectByKey(
        userAndPwdSettings.nameAndPwdSettings[0]?.settings,
        'key'
      )['password.unique'].value === 'true',
    password_min_symbols: convertToInteger(
      convertArrayToObjectByKey(
        userAndPwdSettings.nameAndPwdSettings[0]?.settings,
        'key'
      )['password.min.symbols'].value
    )
  };

  const onSubmit = (data: pwdStrategyFormData) => {
    const payload = {
      ...userAndPwdSettings.nameAndPwdSettings[0],
      settings: transformAdminSettingsToRequestValues(
        data,
        userAndPwdSettings.nameAndPwdSettings[0].settings
      )
    };
    save(payload);
  };

  return (
    <AdminSettingsFormWrapper
      isFetching={false}
      submitForm={onSubmit}
      data={currentValue}
      isLoading={isLoading}
      schema={pwdStrategySchema}
    >
      <FormFields />
    </AdminSettingsFormWrapper>
  );
};

const FormFields = () => {
  const { t } = useTranslation('common', { keyPrefix: 'userAndPassword' });
  const { control } = useFormContext();

  return (
    <>
      <ControlledNumericField
        step={1}
        name="password_min_length"
        label={t('minLength')}
      />
      <ControlledNumericField
        step={1}
        name="password_min_digits"
        label={t('minDigits')}
      />
      <ControlledNumericField
        step={1}
        name="password_min_chars"
        label={t('minChars')}
      />
      <ControlledNumericField
        step={1}
        name="password_min_symbols"
        label={t('minSymbols')}
      />
      <ControlledNumericField
        step={1}
        name="password_max_retry"
        label={t('maxFail')}
      />
      <ControlledNumericField
        step={1}
        name="password_validity"
        label={t('expiredPwd')}
      />
      <ControlledCheckbox
        control={control}
        name="password_unique"
        label={t('newPwdNeedsToBeDifferent')}
      />
    </>
  );
};
